<script setup lang="ts">
    interface Props {
        readonly blok: AImageCardComponentStoryblok & { readonly url?: string };
    }

    const props = defineProps<Props>();

    const linkUrl = computed(() => {
        const { url, link } = props.blok;
        if (!url && link?.linktype !== 'email' && link?.linktype !== 'asset' && !link?.id && !link?.url) {
            return '';
        }

        return useStoryblokUrl(url || link);
    });

    const isAssetLink = computed(() => !props.blok.url && props.blok.link?.linktype === 'asset');
</script>

<template>
    <NuxtLink
        v-if="linkUrl"
        v-editable="blok"
        :to="linkUrl"
        :external="isAssetLink"
        :target="blok.link?.target === '_blank' || blok.externalLink === true ? '_blank' : '_self'"
        class="image-card"
        :class="{ '--banner': blok.isBanner, '--reduce-shadow': blok.reduceShadow }"
    >
        <div class="content">
            <div class="title-wrapper">
                <h3
                    :class="{
                        ['--title-' + blok.titleSize]: blok.titleSize,
                        ['--title-desktop-' + blok.titleSizeDesktop]: blok.titleSizeDesktop
                    }"
                >
                    {{ blok.title }}
                </h3>
                <img
                    v-if="blok.isBanner"
                    src="~/assets/icons/chevron-right.svg"
                    alt="chevron right"
                    aria-hidden="true"
                    :class="{
                        ['--icon-' + blok.titleSize]: blok.titleSize,
                        ['--icon-desktop-' + blok.titleSizeDesktop]: blok.titleSizeDesktop
                    }"
                />
            </div>

            <div
                v-if="blok.text"
                class="description"
            >
                <p>{{ blok.text }}</p>
                <img
                    v-if="!blok.isBanner"
                    src="~/assets/icons/chevron-right.svg"
                    alt="chevron right"
                    aria-hidden="true"
                />
            </div>
        </div>

        <StoryblokImage
            v-if="blok.image.filename"
            class="backdrop"
            :media="blok.image"
            type="mainPicture"
            :alt="blok.image.alt ? blok.image.alt : ''"
            :aria-hidden="true"
        />
    </NuxtLink>

    <div
        v-else
        class="image-card --no-link"
        :class="{ '--banner': blok.isBanner, '--reduce-shadow': blok.reduceShadow }"
    >
        <div class="content">
            <h3
                :class="{
                    ['--title-' + blok.titleSize]: blok.titleSize,
                    ['--title-desktop-' + blok.titleSizeDesktop]: blok.titleSizeDesktop
                }"
            >
                {{ blok.title }}
            </h3>

            <div
                v-if="blok.text"
                class="description"
            >
                <p>{{ blok.text }}</p>
            </div>
        </div>

        <StoryblokImage
            v-if="blok.image.filename"
            class="backdrop"
            :media="blok.image"
            type="mainPicture"
            :alt="blok.image.alt ? blok.image.alt : ''"
            :aria-hidden="true"
        />
    </div>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/image/image-card.scss';
</style>
